import { useState, useEffect, createContext, useContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Password Gate - Remove this import and wrapper to disable password protection
import PasswordGate from "./components/PasswordGate";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import HomeownerDashboard from "./pages/HomeownerDashboard";
import ContractorDashboard from "./pages/ContractorDashboard";
import ContractorOnboarding from "./pages/ContractorOnboarding";
import ContractorProfileEdit from "./pages/ContractorProfileEdit";
import AdminDashboard from "./pages/AdminDashboard";
import AdminCatalog from "./pages/AdminCatalog";
import AdminContractors from "./pages/AdminContractors";
import AdminLeads from "./pages/AdminLeads";
import AdminWaitlist from "./pages/AdminWaitlist";
import AdminSubscriptionPlans from "./pages/AdminSubscriptionPlans";
import AdminCreditPacks from "./pages/AdminCreditPacks";
import AdminLeadPricing from "./pages/AdminLeadPricing";
import AdminSubscriptionManagement from "./pages/AdminSubscriptionManagement";
import SubmitJobRequest from "./pages/SubmitJobRequest";
import EstimateBuilder from "./pages/EstimateBuilder";
import RequestWizard from "./pages/RequestWizard";
import RequestContact from "./pages/RequestContact";
import PropertyManagers from "./pages/PropertyManagers";
import HomeownerRatings from "./pages/HomeownerRatings";
import QuoteSummary from "./pages/QuoteSummary";
import PostQuoteAuth from "./pages/PostQuoteAuth";
import PublicRatingPage from "./pages/PublicRatingPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      setToken(null);
      localStorage.removeItem("token");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      /* silent - error deliberately ignored */
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, googleLogin, token, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = location.hash;
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      
      if (!sessionId) {
        navigate('/login');
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/session`, {
          headers: { 'X-Session-ID': sessionId },
          withCredentials: true
        });
        
        setUser(response.data);
        
        const role = response.data.role;
        if (role === 'admin') {
          navigate('/admin', { replace: true, state: { user: response.data } });
        } else if (role === 'contractor') {
          navigate('/contractor', { replace: true, state: { user: response.data } });
        } else {
          navigate('/dashboard', { replace: true, state: { user: response.data } });
        }
      } catch (error) {
        toast.error('Authentication failed');
        navigate('/login');
      }
    };

    processSession();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="spinner"></div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'contractor') return <Navigate to="/contractor" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();
  
  // Check for session_id in URL fragment BEFORE rendering routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/estimate" element={<EstimateBuilder />} />
      <Route path="/estimate/:categoryId" element={<EstimateBuilder />} />
      <Route path="/request" element={<RequestWizard />} />
      <Route path="/request/:categorySlug" element={<RequestWizard />} />
      <Route path="/request/contact" element={<RequestContact />} />
      <Route path="/quote-summary" element={<QuoteSummary />} />
      <Route path="/quote-submitted" element={<PostQuoteAuth />} />
      <Route path="/submit-request" element={<SubmitJobRequest />} />
      <Route path="/submit-request/:categoryId" element={<SubmitJobRequest />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['homeowner']}>
          <HomeownerDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/homeowner/ratings" element={
        <ProtectedRoute allowedRoles={['homeowner']}>
          <HomeownerRatings />
        </ProtectedRoute>
      } />
      
      <Route path="/contractor" element={
        <ProtectedRoute allowedRoles={['contractor']}>
          <ContractorDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/contractor/onboarding" element={
        <ProtectedRoute allowedRoles={['contractor']}>
          <ContractorOnboarding />
        </ProtectedRoute>
      } />
      
      <Route path="/contractor/profile/edit" element={
        <ProtectedRoute allowedRoles={['contractor']}>
          <ContractorProfileEdit />
        </ProtectedRoute>
      } />
      
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/catalog" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminCatalog />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/contractors" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminContractors />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/leads" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminLeads />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/waitlist" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminWaitlist />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/subscription-plans" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminSubscriptionPlans />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/credit-packs" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminCreditPacks />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/lead-pricing" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminLeadPricing />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/subscriptions" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminSubscriptionManagement />
        </ProtectedRoute>
      } />
      
      <Route path="/property-managers" element={<PropertyManagers />} />
      
      {/* Public Rating Page - No auth required */}
      <Route path="/rate/:token" element={<PublicRatingPage />} />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      {/* Password Gate - Remove this wrapper to disable password protection */}
      <PasswordGate>
        <AuthProvider>
          <Toaster position="top-right" richColors closeButton />
          <AppRouter />
        </AuthProvider>
      </PasswordGate>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
